/**
 * Slug di rotta tradotti per lingua — fonte unica di verità per gli URL pubblici.
 *
 * Regola concordata con il marketing (Christian Ballabio, 2026-07-28):
 *
 *     /[lingua]/[slug-tradotto-pagina-padre]/[slug-tradotto-sottopagina]
 *
 * Prima ogni lingua usava gli slug italiani (`/de/collezioni`, `/en/chi-siamo`):
 * un utente tedesco leggeva parole italiane nell'URL e Google indicizzava tre
 * lingue sotto gli stessi termini.
 *
 * Le traduzioni NON sono inventate: sono le stesse etichette già usate nei menu
 * e nei titoli del sito (`src/i18n/locales/*.json`) — "Chi siamo / About us /
 * Über uns", "Fiscalità / Tax Benefits / Steuervorteile".
 *
 * In EN e DE la pagina che elenca le confezioni è la stessa che fa da padre alle
 * schede prodotto (`/en/collections` e `/en/collections/roma`), come da specifica.
 * In italiano restano distinte: `/it/collezioni` e `/it/confezioni/roma`.
 *
 * NB: checkout, login, signup, account e auth NON sono localizzati di proposito.
 * Non sono pagine di contenuto indicizzate e `/checkout/success` è cablato negli
 * URL di ritorno di Stripe: rinominarli romperebbe i pagamenti.
 */

export const LOCALES = ["it", "en", "de"] as const;
export type Locale = (typeof LOCALES)[number];

export type PageKey =
  | "collections" | "product" | "about" | "contact" | "fiscal" | "privacy" | "blog" | "impressum";

const SLUGS: Record<PageKey, Record<Locale, string>> = {
  collections: { it: "collezioni", en: "collections", de: "kollektionen" },
  product: { it: "confezioni", en: "collections", de: "kollektionen" },
  about: { it: "chi-siamo", en: "about-us", de: "ueber-uns" },
  contact: { it: "contatti", en: "contact", de: "kontakt" },
  fiscal: { it: "fiscalita", en: "tax-benefits", de: "steuervorteile" },
  privacy: { it: "privacy", en: "privacy", de: "datenschutz" },
  blog: { it: "blog", en: "blog", de: "blog" },
  // Lo slug resta «impressum» in tutte le lingue, e non è una dimenticanza:
  // è il termine con cui la legge tedesca identifica questa pagina, ed è la
  // parola che si cerca per trovarla. Tradurlo la renderebbe più difficile da
  // raggiungere proprio per chi ha il diritto di consultarla.
  impressum: { it: "impressum", en: "impressum", de: "impressum" },
};

/**
 * Slug non più canonici da cui vanno accettati (e reindirizzati) i vecchi link.
 * Include gli slug italiani usati storicamente su tutte le lingue e `collections`,
 * che in italiano era un alias della lista collezioni.
 */
const LEGACY_SLUGS: Record<string, PageKey> = {
  confezioni: "product",
  collezioni: "collections",
  collections: "collections",
  "chi-siamo": "about",
  contatti: "contact",
  fiscalita: "fiscal",
  privacy: "privacy", // canonico in it/en, legacy in de (→ datenschutz)
};

export function isValidLocaleTag(value: string | undefined): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value);
}

function normalizeLocale(locale: string | undefined): Locale {
  return isValidLocaleTag(locale) ? locale : "it";
}

/** Slug canonico di una pagina nella lingua data. */
export function slugFor(page: PageKey, locale: string | undefined): string {
  return SLUGS[page][normalizeLocale(locale)];
}

/** URL completo di una pagina; `sub` è lo slug della sottopagina (es. il prodotto). */
export function pathFor(page: PageKey, locale: string | undefined, sub?: string): string {
  const base = `/${normalizeLocale(locale)}/${slugFor(page, locale)}`;
  return sub ? `${base}/${sub}` : base;
}

/** Scorciatoia per il caso più frequente: la scheda di una confezione. */
export function productPath(locale: string | undefined, slug: string): string {
  return pathFor("product", locale, slug);
}

/**
 * Riconosce a quale pagina appartiene uno slug, in QUALSIASI lingua (canonico o
 * storico). Serve per tradurre un indirizzo da una lingua all'altra senza sapere
 * in partenza da quale lingua si arriva.
 *
 * `hasSub` distingue i due significati che in inglese e tedesco condividono lo
 * stesso slug padre: `/en/collections` è la lista, `/en/collections/roma` è una
 * scheda prodotto.
 */
export function pageForSlug(slug: string, hasSub: boolean): PageKey | null {
  const matches = (Object.keys(SLUGS) as PageKey[]).filter((page) =>
    LOCALES.some((l) => SLUGS[page][l] === slug),
  );
  if (matches.length === 0) return LEGACY_SLUGS[slug] ?? null;
  if (matches.length > 1) return hasSub ? "product" : "collections";
  return matches[0];
}

/**
 * Traduce un indirizzo nella lingua richiesta: `/it/confezioni/roma` con `de`
 * diventa `/de/kollektionen/roma`.
 *
 * Senza questo, cambiando lingua si otteneva `/de/confezioni/roma`, cioè il
 * nuovo prefisso di lingua incollato sullo slug della lingua precedente: un
 * indirizzo che esiste solo grazie al redirect e che mostra all'utente parole
 * della lingua sbagliata. Se lo slug non è riconosciuto (rotte funzionali come
 * checkout o login, che non sono tradotte) il percorso resta com'è.
 */
export function translatePath(pathname: string, toLocale: string): string {
  const target = normalizeLocale(toLocale);
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return `/${target}/`;

  const rest = isValidLocaleTag(segments[0]) ? segments.slice(1) : segments;
  if (rest.length === 0) return `/${target}/`;

  const page = pageForSlug(rest[0], rest.length > 1);
  if (!page) return `/${target}/${rest.join("/")}`; // rotta non localizzata: invariata

  const sub = page === "collections" ? undefined : rest.slice(1).join("/") || undefined;
  return pathFor(page, target, sub);
}

/**
 * Data una lingua, elenca gli slug legacy da reindirizzare con la pagina di
 * destinazione. Esclude quelli che in quella lingua sono già canonici (in
 * italiano `confezioni` è corretto, in inglese no).
 */
export function legacyRedirects(locale: string): Array<{ from: string; page: PageKey }> {
  const canonical = new Set(Object.keys(SLUGS).map((k) => slugFor(k as PageKey, locale)));
  return Object.entries(LEGACY_SLUGS)
    .filter(([from]) => !canonical.has(from))
    .map(([from, page]) => ({ from, page }));
}
